import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Award,
  ChevronLeft,
  ChevronRight,
  Mail,
  MessageCircle,
  Phone,
  Search,
  Trophy,
  Upload,
  Users,
} from "lucide-react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Toast, type ToastKind } from "@/components/Toast";
import { api } from "@/lib/api";
import { useClients } from "@/hooks/useClients";
import { useClientsStats } from "@/hooks/useClientsStats";
import { formatCurrency, formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

export default function Clients() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [levelMin, setLevelMin] = useState<number | undefined>();
  const [levelMax, setLevelMax] = useState<number | undefined>();
  const [onlyCommunity, setOnlyCommunity] = useState<boolean | undefined>();
  const [importing, setImporting] = useState(false);
  const [toast, setToast] = useState<{ kind: ToastKind; message: string } | null>(null);

  const handleImport = async (file: File, mode: "replace" | "merge") => {
    setImporting(true);
    try {
      const res = await api.importClientsCsv(file, mode);
      qc.invalidateQueries({ queryKey: ["clients"] });
      qc.invalidateQueries({ queryKey: ["clients", "stats"] });
      setToast({
        kind: "success",
        message: `Import ok: ${res.rows_imported} righe (prima: ${res.rows_before}, dopo: ${res.rows_after}). Backup: ${res.backup_path.split("/").pop() || "—"}`,
      });
    } catch (e) {
      const msg =
        (e as { body?: { detail?: string }; message?: string })?.body?.detail ??
        (e as Error)?.message ??
        String(e);
      setToast({ kind: "error", message: `Errore import: ${msg}` });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadCurrentCsv = () => {
    // Apre l'export in nuova tab (backend serve il CSV via FileResponse se
    // aggiungeremo un endpoint dedicato; per ora linkiamo al path noto)
    setToast({
      kind: "info",
      message: "Export CSV backend: esegui `scp` dal VPS `tournaments_data/clients/client_list.csv`",
    });
  };

  const pageSize = 50;

  // debounce search
  if (search !== searchDebounced) {
    setTimeout(() => setSearchDebounced(search), 300);
  }

  const { data, isLoading, isFetching } = useClients({
    page,
    page_size: pageSize,
    search: searchDebounced || undefined,
    level_min: levelMin,
    level_max: levelMax,
    only_community: onlyCommunity,
  });
  const stats = useClientsStats();

  const resetFilters = () => {
    setSearch("");
    setSearchDebounced("");
    setLevelMin(undefined);
    setLevelMax(undefined);
    setOnlyCommunity(undefined);
    setPage(1);
  };

  const total = data?.pagination.total ?? 0;
  const totalPages = data?.pagination.total_pages ?? 1;

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
            <Users className="h-6 w-6 text-moving-500" />
            Clienti
          </h1>
          <p className="text-sm text-slate-500">
            {stats.data
              ? `${formatNumber(stats.data.total)} totali · ${formatNumber(
                  stats.data.email_contactable,
                )} email · ${formatNumber(stats.data.whatsapp_contactable)} WA`
              : "Caricamento…"}
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (
                !confirm(
                  `Importare "${f.name}" e SOSTITUIRE il CSV clienti attuale? (viene fatto backup automatico)`,
                )
              ) {
                if (fileRef.current) fileRef.current.value = "";
                return;
              }
              handleImport(f, "replace");
            }}
          />
          <Link to="/clients/history">
            <Button variant="outline" size="sm">
              <Trophy className="h-4 w-4" />
              Giocatori storici
            </Button>
          </Link>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadCurrentCsv}
            disabled={importing}
          >
            Export CSV
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => fileRef.current?.click()}
            loading={importing}
          >
            <Upload className="h-4 w-4" />
            Importa CSV Playtomic
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-court-200 bg-court-50 p-3 text-xs text-court-900">
        <strong>Sincronizzazione clienti:</strong> Playtomic non espone
        un'API pubblica per export automatico. Workflow corrente → esporta il
        CSV dal pannello Playtomic, premi "Importa CSV Playtomic" qui sopra. Il
        sistema fa backup del precedente in{" "}
        <code className="font-mono">clients/client_list.backup_&lt;timestamp&gt;.csv</code>{" "}
        prima di sovrascrivere. Le blacklist email/WhatsApp restano invariate.
      </div>

      {/* Stats mini */}
      {stats.data ? (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <MiniStat
            label="Totali"
            value={formatNumber(stats.data.total)}
            icon={<Users className="h-4 w-4" />}
          />
          <MiniStat
            label="Email contattabili"
            value={formatNumber(stats.data.email_contactable)}
            icon={<Mail className="h-4 w-4" />}
          />
          <MiniStat
            label="WA contattabili"
            value={formatNumber(stats.data.whatsapp_contactable)}
            icon={<MessageCircle className="h-4 w-4" />}
          />
          <MiniStat
            label="Disiscritti"
            value={`${formatNumber(stats.data.unsubscribed_email)} + ${formatNumber(stats.data.unsubscribed_whatsapp)}`}
            icon={<Phone className="h-4 w-4" />}
            hint="email / WA"
          />
        </div>
      ) : null}

      {/* Filtri */}
      <Card>
        <CardBody className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_auto_auto]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder="Cerca nome, email, telefono…"
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="levelMin" className="text-xs">
                Livello min
              </Label>
              <Input
                id="levelMin"
                type="number"
                step="0.1"
                min={0}
                max={7}
                value={levelMin ?? ""}
                onChange={(e) => {
                  setLevelMin(
                    e.target.value === "" ? undefined : parseFloat(e.target.value),
                  );
                  setPage(1);
                }}
                className="w-20"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="levelMax" className="text-xs">
                max
              </Label>
              <Input
                id="levelMax"
                type="number"
                step="0.1"
                min={0}
                max={7}
                value={levelMax ?? ""}
                onChange={(e) => {
                  setLevelMax(
                    e.target.value === "" ? undefined : parseFloat(e.target.value),
                  );
                  setPage(1);
                }}
                className="w-20"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              Azzera
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {[
              { v: undefined, l: "Tutti" },
              { v: true, l: "Solo community" },
              { v: false, l: "Solo non-community" },
            ].map((opt, i) => (
              <button
                key={i}
                onClick={() => {
                  setOnlyCommunity(opt.v);
                  setPage(1);
                }}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  onlyCommunity === opt.v
                    ? "bg-moving-500 text-white"
                    : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                )}
              >
                {opt.l}
              </button>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Tabella */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>
              Risultati ({formatNumber(total)}
              {isFetching ? "…" : ""})
            </CardTitle>
            <div className="text-xs text-slate-500">
              pag. {page} / {totalPages}
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 text-sm text-slate-500">Caricamento…</div>
          ) : total === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              Nessun cliente trovato.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Nome</th>
                    <th className="px-3 py-2 text-left font-medium">Contatto</th>
                    <th className="px-3 py-2 text-left font-medium">
                      <span className="inline-flex items-center gap-1">
                        <Award className="h-3.5 w-3.5" />
                        Livello
                      </span>
                    </th>
                    <th className="px-3 py-2 text-right font-medium">Borsellino</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data?.items ?? []).map((c) => (
                    <tr key={c.playtomic_id} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">
                          {c.name}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          {c.is_community ? (
                            <span className="rounded bg-court-100 px-1 py-0.5 text-[10px] font-semibold text-court-700">
                              COMMUNITY
                            </span>
                          ) : null}
                          <span className="font-mono">{c.playtomic_id}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {c.email ? (
                          <div className="flex items-center gap-1 text-slate-700">
                            <Mail className="h-3 w-3 shrink-0 text-slate-400" />
                            <span className="font-mono">{c.email}</span>
                          </div>
                        ) : null}
                        {c.phone_number ? (
                          <div className="flex items-center gap-1 text-slate-500">
                            <MessageCircle className="h-3 w-3 shrink-0 text-slate-400" />
                            <span className="font-mono">{c.phone_number}</span>
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        {c.padel_level ? (
                          <span className="rounded-md bg-moving-50 px-2 py-0.5 text-xs font-semibold text-moving-700">
                            {c.padel_level.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-xs font-mono text-slate-600">
                        {c.wallet_balance > 0
                          ? formatCurrency(c.wallet_balance)
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
        {/* Paginazione */}
        {totalPages > 1 ? (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2">
            <div className="text-xs text-slate-500">
              Mostra {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}{" "}
              di {formatNumber(total)}
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={page === 1}
              >
                «
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <span className="px-3 text-sm text-slate-600">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
              >
                »
              </Button>
            </div>
          </div>
        ) : null}
      </Card>

      {toast ? (
        <Toast
          kind={toast.kind}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      ) : null}
    </div>
  );
}

function MiniStat({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <Card>
      <CardBody>
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          {icon}
          {label}
        </div>
        <div className="mt-1 text-xl font-semibold text-slate-900">{value}</div>
        {hint ? <div className="text-xs text-slate-400">{hint}</div> : null}
      </CardBody>
    </Card>
  );
}
